import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';

import { ReactComponent as FilterIcon } from '../../../assets/FilterIcon.svg';
import { ReactComponent as ResetIcon } from '../../../assets/ResetIcon.svg';
import { ReactComponent as BellOutlineIcon } from '../../../assets/BellOutline.svg';
import { ReactComponent as LocationIcon } from '../../../assets/LocationIcon.svg';
import { ReactComponent as ViewGridOutlineIcon } from '../../../assets/ViewGridOutline.svg';
import { ReactComponent as CoinsIcon } from '../../../assets/coins.svg';

import css from './MainPanelHeader.module.css';

const CurrencySelector = ({ selectedCurrency, options = [], onChange }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const normalized = (selectedCurrency || 'ORIGINAL').toUpperCase();
  const displayLabel =
    normalized === 'ORIGINAL'
      ? intl.formatMessage({ id: 'MainPanelHeader.currencyButtonLabel', defaultMessage: 'Currency' })
      : normalized;

  useEffect(() => {
    const handleClickOutside = event => {
      if (open && wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  }, []);

  const handleMouseEnter = useCallback(() => {
    clearTimeout(closeTimer.current);
    clearTimeout(openTimer.current);
    if (open) return;
    openTimer.current = setTimeout(() => setOpen(true), 200);
  }, [open]);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  }, []);

  const toggle = () => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleSelect = currency => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    if (onChange) {
      onChange(currency);
    }
    setOpen(false);
  };

  return (
    <div className={css.currencySelector} ref={wrapperRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        type="button"
        className={classNames(css.filterButton, css.currencyButton)}
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={intl.formatMessage({ id: 'MainPanelHeader.currencyLabel' })}
      >
        <span className={css.currencyLabel}>{displayLabel}</span>
        <CoinsIcon className={css.currencyIcon} aria-hidden="true" />
      </button>
      <ul className={classNames(css.currencyMenu, { [css.currencyMenuOpen]: open })} role="listbox">
        {options.map(option => {
          const code = option.toUpperCase();
          const isActive = code === normalized;
          const optionLabel =
            code === 'ORIGINAL'
              ? intl.formatMessage({ id: 'MainPanelHeader.currencyOriginal', defaultMessage: 'Original' })
              : code;
          return (
            <li key={code}>
              <button
                type="button"
                role="option"
                aria-selected={isActive}
                className={classNames(css.currencyOption, { [css.currencyOptionActive]: isActive })}
                onClick={() => handleSelect(code)}
              >
                {optionLabel}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const RemindMeButton = ({ remindMeAction, className, source }) => {
  if (!remindMeAction) {
    return null;
  }

  const showStatus =
    remindMeAction.statusMessage && (!remindMeAction.statusSource || remindMeAction.statusSource === source);

  return (
    <div className={classNames(css.remindWrapper, className)}>
      <button
        type="button"
        className={css.filterButton}
        onClick={() => remindMeAction.onClick && remindMeAction.onClick(source)}
        disabled={remindMeAction.disabled}
      >
        <span className={css.filterLabel}>{remindMeAction.label}</span>
        <BellOutlineIcon className={classNames(css.filterIcon, css.remindIcon)} aria-hidden="true" />
      </button>
      {showStatus ? (
        <span
          className={classNames(css.remindStatus, {
            [css.remindStatusSuccess]: remindMeAction.status === 'success',
            [css.remindStatusError]: remindMeAction.status === 'error',
          })}
          role="status"
          aria-live="polite"
        >
          {remindMeAction.statusMessage}
        </span>
      ) : null}
    </div>
  );
};

/**
 * MainPanelHeader component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {React.Node} props.children - The children
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.isSortByActive - Whether the sort by is active
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The results count
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {React.Node} props.noResultsInfo - The no results info
 * @returns {JSX.Element}
 */
const MainPanelHeader = props => {
  const {
    rootClassName,
    className,
    children,
    sortByComponent,
    isSortByActive,
    listingsAreLoaded,
    resultsCount,
    searchInProgress = false,
    noResultsInfo,
    onOpenFilters,
    onResetFilters,
    isFiltersOpen = false,
    remindMeAction,
    hasActiveFilters = false,
    activeFilterCount = 0,
    onToggleMapView,
    isMapViewActive = false,
    selectedCurrency,
    availableCurrencies,
    onCurrencyChange,
  } = props;

  const intl = useIntl();

  const classes = classNames(rootClassName || css.root, className);
  const resultsContent = searchInProgress ? (
    <FormattedMessage id="MainPanelHeader.loadingResults" />
  ) : (
    <FormattedMessage id="MainPanelHeader.items" values={{ count: resultsCount || 0 }} />
  );

  const handleFilterClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onOpenFilters) {
      onOpenFilters();
    }
  };

  const handleResetClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onResetFilters) {
      onResetFilters(e);
    }
  };

  const handleMapToggleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleMapView) {
      onToggleMapView();
    }
  };

  const MapToggleIcon = isMapViewActive ? ViewGridOutlineIcon : LocationIcon;
  const mapButtonLabelId = isMapViewActive ? 'MainPanelHeader.grid' : 'MainPanelHeader.map';

  const remindActionNode = remindMeAction ? (
    <RemindMeButton remindMeAction={remindMeAction} source="mainPanel" />
  ) : null;
  const currencySelector =
    onCurrencyChange && availableCurrencies?.length ? (
      <CurrencySelector
        selectedCurrency={selectedCurrency}
        options={availableCurrencies}
        onChange={onCurrencyChange}
      />
    ) : null;
  const currencySelectorMobile =
    onCurrencyChange && availableCurrencies?.length ? (
      <div className={css.currencySelectorMobile}>
        <CurrencySelector
          selectedCurrency={selectedCurrency}
          options={availableCurrencies}
          onChange={onCurrencyChange}
        />
      </div>
    ) : null;

  const sortContent = isSortByActive && sortByComponent ? sortByComponent : null;

  return (
    <div className={classes}>
      <div className={css.controlsRow}>
        <div className={css.filterCell}>
          <button
            type="button"
            className={classNames(css.filterButton, {
              [css.filterButtonActive]: isFiltersOpen,
              [css.filterButtonHasActiveFilters]: hasActiveFilters,
            })}
            onClick={handleFilterClick}
            aria-label={intl.formatMessage({ id: 'MainPanelHeader.filter' })}
          >
            <FilterIcon className={css.filterIcon} aria-hidden="true" />
            <span className={css.filterLabel}>
              <FormattedMessage id="MainPanelHeader.filter" />
            </span>
            {activeFilterCount > 0 ? (
              <span className={css.filterBadge}>{activeFilterCount}</span>
            ) : null}
          </button>
          {onToggleMapView ? (
            <button
              type="button"
              className={classNames(css.filterButton, css.mapButton, {
                [css.mapButtonGrid]: isMapViewActive,
              })}
              onClick={handleMapToggleClick}
              aria-pressed={isMapViewActive}
              aria-label={intl.formatMessage({ id: mapButtonLabelId })}
            >
              <MapToggleIcon className={css.mapIcon} aria-hidden="true" />
              <span className={css.filterLabel}>
                <FormattedMessage id={mapButtonLabelId} />
              </span>
            </button>
          ) : null}
          {onResetFilters ? (
            <button
              type="button"
              className={classNames(css.filterButton, css.resetButton)}
              onClick={handleResetClick}
              aria-label={intl.formatMessage({ id: 'MainPanelHeader.reset' })}
            >
              <ResetIcon className={css.resetIcon} aria-hidden="true" />
              <span className={css.resetLabel}>
                <FormattedMessage id="MainPanelHeader.reset" />
              </span>
            </button>
          ) : null}
        </div>
        <div className={css.resultsCell}>{resultsContent}</div>
        <div className={css.sortCell} data-testid="sort-wrapper">
          <div className={css.sortControls}>
            {currencySelector}
            {remindActionNode}
            {sortContent}
          </div>
        </div>
      </div>

      <div className={css.mobileInfoRow}>
        <div className={css.resultsCellMobile}>{resultsContent}</div>
        {currencySelectorMobile}
      </div>

      {children}

      {noResultsInfo ? <div className={css.noResults}>{noResultsInfo}</div> : null}
    </div>
  );
};

export default MainPanelHeader;
